import { useEffect, useMemo, useRef, useState } from 'react';
import type { JSONSchema } from '@/lib/types';
import { ChevronDown, ChevronRight, Pencil } from 'lucide-react';
import clsx from 'clsx';

/* ------------------------------------------------------------------------ */
/* Schema helpers (kept local — `JsonSchemaForm` has parallel copies)         */
/* ------------------------------------------------------------------------ */

function resolveSchema(
  schema: JSONSchema | undefined,
  rootSchema: JSONSchema
): JSONSchema | undefined {
  if (!schema) return schema;
  if (!schema.$ref) return schema;
  if (!schema.$ref.startsWith('#/')) return schema;
  const parts = schema.$ref.slice(2).split('/');
  let cur: unknown = rootSchema;
  for (const p of parts) {
    if (cur && typeof cur === 'object' && p in (cur as Record<string, unknown>)) {
      cur = (cur as Record<string, unknown>)[p];
    } else {
      return schema;
    }
  }
  return resolveSchema(cur as JSONSchema | undefined, rootSchema);
}

function pickScalarType(s: JSONSchema | undefined): 'string' | 'number' | 'integer' | 'boolean' {
  if (!s) return 'string';
  const t = Array.isArray(s.type) ? s.type[0] : s.type;
  if (t === 'number') return 'number';
  if (t === 'integer') return 'integer';
  if (t === 'boolean') return 'boolean';
  return 'string';
}

/* ------------------------------------------------------------------------ */
/* Top-level                                                                 */
/* ------------------------------------------------------------------------ */

interface Props {
  /** Root schema (used for `$ref` resolution). */
  schema: JSONSchema | undefined;
  /** Current value tree. */
  value: unknown;
  /** Called with the next value when any field commits an edit. */
  onChange: (next: unknown) => void;
  /** Disable double-click → edit. */
  readOnly?: boolean;
}

/**
 * Compact JSON-tree view of a value with per-field double-click-to-edit
 * affordances. Designed for `hasSnapshot` commands so the user can see
 * the current values returned by the device's snapshot lambda in their
 * native JSON shape and tweak individual fields without scanning a
 * full form.
 *
 * <h2>Editing UX</h2>
 *
 * <ul>
 *   <li>Each scalar leaf renders as `key: value` on one line.
 *   <li>Double-click (or click the pencil) on a value swaps it for an
 *       inline {@code <input>} / {@code <select>}.
 *   <li>Enter / blur commits, Escape cancels.
 *   <li>Booleans toggle on single click for friction-free flipping.
 *   <li>Objects render as collapsible nested blocks (default expanded).
 *   <li>Arrays render their JSON form; editing wholesale arrays is left
 *       to the regular {@code JsonSchemaForm} — the snapshot use case
 *       has none in the sample app, so this is intentional minimalism.
 * </ul>
 */
export function InlineJsonEditor({
  schema,
  value,
  onChange,
  readOnly = false,
}: Props): JSX.Element {
  const rootSchema = schema ?? ({} as JSONSchema);
  const resolved = resolveSchema(schema, rootSchema);
  if (!resolved) {
    return <p className="text-xs text-ink-muted">无参数。</p>;
  }
  return (
    <div className="rounded border border-line bg-bg-soft/40 p-2 font-mono text-xs">
      <Node
        schema={resolved}
        value={value}
        rootSchema={rootSchema}
        readOnly={readOnly}
        onChange={onChange}
        depth={0}
      />
    </div>
  );
}

/* ------------------------------------------------------------------------ */
/* Recursive node                                                            */
/* ------------------------------------------------------------------------ */

function Node({
  schema,
  value,
  onChange,
  rootSchema,
  readOnly,
  depth,
}: {
  schema: JSONSchema | undefined;
  value: unknown;
  onChange: (next: unknown) => void;
  rootSchema: JSONSchema;
  readOnly: boolean;
  depth: number;
}): JSX.Element {
  const r = resolveSchema(schema, rootSchema);
  const t = Array.isArray(r?.type) ? r?.type[0] : r?.type;
  if (r?.properties || t === 'object') {
    return (
      <ObjectNode
        schema={r ?? {}}
        value={(value as Record<string, unknown> | null | undefined) ?? {}}
        rootSchema={rootSchema}
        readOnly={readOnly}
        onChange={onChange}
        depth={depth}
      />
    );
  }
  // Arrays are intentionally left as a single inline pre-formatted line;
  // the snapshot use case in this codebase has no array fields.
  if (t === 'array') {
    return (
      <pre className="overflow-auto whitespace-pre-wrap break-words rounded bg-bg-card px-1 py-0.5 text-[11px]">
        {JSON.stringify(value ?? [], null, 2)}
      </pre>
    );
  }
  return (
    <ScalarLeaf
      schema={r}
      value={value}
      readOnly={readOnly}
      onChange={onChange}
    />
  );
}

/* ------------------------------------------------------------------------ */
/* Object renderer                                                           */
/* ------------------------------------------------------------------------ */

function ObjectNode({
  schema,
  value,
  rootSchema,
  readOnly,
  onChange,
  depth,
}: {
  schema: JSONSchema;
  value: Record<string, unknown>;
  rootSchema: JSONSchema;
  readOnly: boolean;
  onChange: (next: Record<string, unknown>) => void;
  depth: number;
}): JSX.Element {
  const [collapsed, setCollapsed] = useState(false);
  const props = schema.properties ?? {};
  const keys = useMemo(() => {
    const declared = Object.keys(props);
    const observed = Object.keys(value ?? {}).filter((k) => !declared.includes(k));
    return [...declared, ...observed];
  }, [props, value]);

  if (keys.length === 0) {
    return <span className="text-ink-dim">{'{}'}</span>;
  }

  return (
    <div className={depth === 0 ? '' : 'pl-3'}>
      <button
        type="button"
        className="inline-flex items-center gap-0.5 text-ink-dim hover:text-ink"
        onClick={() => setCollapsed((v) => !v)}
        title={collapsed ? '展开' : '折叠'}
      >
        {collapsed ? (
          <ChevronRight className="h-3 w-3" />
        ) : (
          <ChevronDown className="h-3 w-3" />
        )}
        <span>{'{'}</span>
        {collapsed && <span className="text-ink-dim">…{keys.length} 字段…{'}'}</span>}
      </button>
      {!collapsed && (
        <>
          <ul className="space-y-0.5 pl-3">
            {keys.map((k) => {
              const childSchema = (props as Record<string, JSONSchema>)[k];
              return (
                <li key={k} className="flex items-start gap-1 leading-snug">
                  <span className="shrink-0 text-accent">"{k}"</span>
                  <span className="shrink-0 text-ink-dim">:</span>
                  <span className="min-w-0 flex-1">
                    <Node
                      schema={childSchema}
                      value={value?.[k]}
                      rootSchema={rootSchema}
                      readOnly={readOnly}
                      onChange={(nextField) => {
                        const next: Record<string, unknown> = { ...(value ?? {}) };
                        if (nextField === undefined) delete next[k];
                        else next[k] = nextField;
                        onChange(next);
                      }}
                      depth={depth + 1}
                    />
                  </span>
                </li>
              );
            })}
          </ul>
          <div className="text-ink-dim">{'}'}</div>
        </>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------------ */
/* Scalar leaf (the actual double-click-to-edit cell)                        */
/* ------------------------------------------------------------------------ */

function ScalarLeaf({
  schema,
  value,
  readOnly,
  onChange,
}: {
  schema: JSONSchema | undefined;
  value: unknown;
  readOnly: boolean;
  onChange: (next: unknown) => void;
}): JSX.Element {
  const type = pickScalarType(schema);
  const enumChoices = Array.isArray(schema?.enum) ? (schema!.enum as unknown[]) : null;
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const inputRef = useRef<HTMLInputElement | HTMLSelectElement | null>(null);

  useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus();
      if (inputRef.current instanceof HTMLInputElement) inputRef.current.select();
    }
  }, [editing]);

  const startEdit = (): void => {
    if (readOnly) return;
    setDraft(value === undefined || value === null ? '' : String(value));
    setEditing(true);
  };

  const commit = (raw: string): void => {
    setEditing(false);
    let next: unknown = raw;
    if (type === 'number' || type === 'integer') {
      if (raw === '') {
        next = undefined;
      } else {
        const n = type === 'integer' ? parseInt(raw, 10) : Number(raw);
        if (Number.isFinite(n)) next = n;
        else return; // ignore invalid input — leave value untouched
      }
    } else if (type === 'boolean') {
      next = raw === 'true';
    }
    if (next !== value) onChange(next);
  };

  // Booleans get a click-to-toggle affordance — modal editing is overkill
  // for a two-state value.
  if (type === 'boolean' && !readOnly) {
    const bool = !!value;
    return (
      <button
        type="button"
        className={clsx(
          'rounded px-1 py-0.5 transition',
          bool
            ? 'bg-emerald-500/10 text-emerald-500 hover:bg-emerald-500/20'
            : 'bg-rose-500/10 text-rose-500 hover:bg-rose-500/20'
        )}
        onClick={() => onChange(!bool)}
        title="点击切换"
      >
        {bool ? 'true' : 'false'}
      </button>
    );
  }

  if (editing) {
    if (enumChoices) {
      return (
        <select
          ref={(el) => (inputRef.current = el)}
          className="rounded border border-accent bg-bg-card px-1 py-0.5 text-xs"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={() => commit(draft)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') commit(draft);
            if (e.key === 'Escape') setEditing(false);
          }}
        >
          {enumChoices.map((c) => (
            <option key={String(c)} value={String(c)}>
              {String(c)}
            </option>
          ))}
        </select>
      );
    }
    return (
      <input
        ref={(el) => (inputRef.current = el)}
        type={type === 'number' || type === 'integer' ? 'number' : 'text'}
        className="rounded border border-accent bg-bg-card px-1 py-0.5 text-xs"
        style={{ minWidth: 80, width: Math.max(80, draft.length * 8) }}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={() => commit(draft)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') commit(draft);
          if (e.key === 'Escape') setEditing(false);
        }}
      />
    );
  }

  const display = renderScalar(value, type);
  return (
    <span
      className={clsx(
        'group inline-flex max-w-full items-center gap-1 rounded px-1 transition',
        !readOnly && 'cursor-text hover:bg-accent-soft/40'
      )}
      onDoubleClick={startEdit}
      title={readOnly ? '只读' : '双击编辑'}
    >
      <span className={typeClass(type, value)}>{display}</span>
      {!readOnly && (
        <button
          type="button"
          className="rounded p-0.5 opacity-0 transition hover:bg-bg-soft group-hover:opacity-100"
          onClick={startEdit}
          title="编辑"
        >
          <Pencil className="h-2.5 w-2.5 text-ink-dim" />
        </button>
      )}
    </span>
  );
}

function renderScalar(value: unknown, type: string): string {
  if (value === undefined || value === null) return type === 'string' ? '""' : 'null';
  if (type === 'string') return `"${String(value)}"`;
  return String(value);
}

function typeClass(type: string, value: unknown): string {
  if (value === undefined || value === null) return 'text-ink-dim';
  switch (type) {
    case 'string':
      return 'text-emerald-500';
    case 'number':
    case 'integer':
      return 'text-amber-500';
    case 'boolean':
      return (value as boolean) ? 'text-emerald-500' : 'text-rose-500';
    default:
      return 'text-ink';
  }
}
